import React, { useEffect, useRef, useState } from 'react';
import MaskedInput, { conformToMask } from 'react-text-mask';
import { useForm, useFormState } from 'react-final-form';
import { Icon } from '../../Icon';
import { scrollInputToCenter } from '../../../utils/helpers/common';
import {
  StyledPhoneMask,
  StyledTextField,
  StyledTextLabel,
  TextFieldContainer
} from './styled';
import { MaskedPhoneInputProps } from './types';

export const MaskedPhoneInput: React.FC<MaskedPhoneInputProps> = ({
  icon,
  name,
  onClick,
  disabled = false,
  placeholder,
  showPhoneMask = false
}) => {
  const { change } = useForm();
  const { values } = useFormState({
    subscription: { values: true }
  });
  const mask = [
    '(',
    '0',
    /[1-9]/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/
  ];
  const value = values[name] === undefined ? '' : values[name];
  const formattedValue = conformToMask(value, mask).conformedValue;
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<MaskedInput | null>(null);
  const inputElement = inputRef.current?.inputElement;

  useEffect(() => {
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [inputRef.current]);

  const focusInput = () => {
    setIsFocused(true);
    inputElement?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    change(name, rawValue);
  };

  const scrollHandler = () => scrollInputToCenter(inputElement);

  useEffect(() => {
    handleInputChange({
      target: { value }
    } as React.ChangeEvent<HTMLInputElement>);
  }, [value]);

  const phoneMask = '(000) 000-00-00';

  return (
    <TextFieldContainer data-cy="text-field-container">
      <StyledTextField onClick={onClick} data-cy="text-field">
        <StyledTextLabel
          role="presentation"
          onClick={focusInput}
          $isActive={isFocused || value}
        >
          {placeholder}
        </StyledTextLabel>
        <MaskedInput
          mask={mask}
          guide={false}
          ref={inputRef}
          pattern="[0-9]*"
          autoComplete="off"
          inputMode="numeric"
          disabled={disabled}
          data-cy="phone-input"
          onFocus={() => {
            setIsFocused(true);
            scrollHandler();
          }}
          onChange={handleInputChange}
          className="masked-input-field"
          value={value ? formattedValue : value}
        />
        {showPhoneMask && !value && isFocused && (
          <StyledPhoneMask tag="body_1">{phoneMask}</StyledPhoneMask>
        )}
      </StyledTextField>

      {icon && (
        <Icon IconComponent={icon} width={35} height={35} indent={false} />
      )}
    </TextFieldContainer>
  );
};
